import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Globe, Layers, Zap, Check, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import { resolveGroupMembers } from '../lib/destinationGroups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public SEO page for an admin-defined destination group (e.g. Indonesia with
// Standard / Lite / Premium variants). Lists every member with its label +
// descriptor and a "from" price so Google can index the brand name once per
// group instead of per variant.
const DestinationGroupPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { allCountries, destinationGroups, startingPrices, formatPrice } = useApp();
  const [fallbackGroup, setFallbackGroup] = useState(null);

  // If the groups list hasn't hydrated yet (direct deep-link / SEO crawl)
  // fetch this group's details on its own.
  useEffect(() => {
    if (destinationGroups.length) return;
    let cancelled = false;
    axios.get(`${API}/destination-groups`).then((r) => {
      if (cancelled || !r.data?.success) return;
      const g = (r.data.groups || []).find((x) => x.id === groupId);
      if (g) setFallbackGroup(g);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [destinationGroups.length, groupId]);

  const group = useMemo(
    () => destinationGroups.find((g) => g.id === groupId) || fallbackGroup,
    [destinationGroups, groupId, fallbackGroup],
  );

  const members = useMemo(() => resolveGroupMembers(group, allCountries), [group, allCountries]);

  const primaryMember = members.find((m) => m.country_id === group?.primary_country_id) || members[0];
  const heroImage = primaryMember?.country?.image;

  const groupMinPrice = useMemo(() => {
    const prices = members
      .map((m) => startingPrices[m.country_id])
      .filter((v) => typeof v === 'number' && v > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [members, startingPrices]);

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Navbar />
        <div className="pt-28 pb-20 text-center px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-3">Group not found</h1>
          <button
            onClick={() => navigate('/destinations')}
            className="px-5 py-2.5 bg-black text-white rounded-full font-bold"
          >
            Browse all destinations
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const title = `${group.name} eSIM — Compare plan tiers`;
  const description = `Compare ${members.length} ${group.name} eSIM plan tiers side-by-side${groupMinPrice ? ` — from ${formatPrice(groupMinPrice)}` : ''}. Instant activation, no roaming charges.`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950" data-testid={`group-page-${group.id}`}>
      <SEO
        title={title}
        description={description}
        canonical={`/destinations/group/${group.id}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'All Destinations', url: '/destinations' },
          { name: group.name, url: `/destinations/group/${group.id}` },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          {heroImage ? (
            <img
              src={heroImage}
              alt={group.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-600 to-teal-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/55 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/destinations')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
            data-testid="group-page-back"
          >
            <ArrowLeft size={20} />
            <span>All destinations</span>
          </button>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
            <Layers size={14} className="text-white" />
            <span className="text-xs font-bold text-white uppercase tracking-wider">{members.length} plan tiers</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white drop-shadow mb-2">{group.name}</h1>
          {groupMinPrice ? (
            <p className="text-white/90 text-lg">
              Plans from <span className="font-black">{formatPrice(groupMinPrice)}</span>
            </p>
          ) : null}
        </div>
      </section>

      {/* Variants */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-6">Compare plan tiers</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {members.map((m) => {
              const country = m.country;
              const price = startingPrices[m.country_id];
              return (
                <button
                  key={m.country_id}
                  onClick={() => navigate(`/destinations/${country.id}`)}
                  className="group text-left bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 overflow-hidden hover:border-black dark:hover:border-white hover:shadow-xl transition-all"
                  data-testid={`group-page-variant-${country.id}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {country.isRegion ? (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                        <Globe size={48} className="text-white" />
                      </div>
                    ) : (
                      <img
                        src={country.image || country.flag}
                        alt={m.label || country.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <h3 className="text-xl font-black text-gray-900 dark:text-neutral-100 truncate">
                        {m.label || country.name}
                      </h3>
                      {price ? (
                        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          from {formatPrice(price)}
                        </span>
                      ) : null}
                    </div>
                    {m.descriptor ? (
                      <p className="text-sm text-gray-600 dark:text-neutral-400 line-clamp-3">{m.descriptor}</p>
                    ) : (
                      <p className="text-sm text-gray-400 dark:text-neutral-500 italic">Instant eSIM — tap to view plans</p>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-neutral-100 group-hover:gap-2.5 transition-all">
                      View plans <ArrowRight size={14} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Why Connectify */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Zap, title: 'Instant activation', text: 'Scan the QR and you\'re online in minutes.' },
              { icon: Check, title: 'No contracts', text: 'Pay once per trip — top up any time.' },
              { icon: Globe, title: 'Local network speed', text: 'Rides the same towers as locals.' },
            ].map((f, i) => (
              <div key={i} className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-100 dark:border-neutral-800 p-5">
                <f.icon size={20} className="text-emerald-500 mb-2" />
                <h4 className="font-bold text-gray-900 dark:text-neutral-100">{f.title}</h4>
                <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinationGroupPage;
